import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import { toast } from 'react-hot-toast';
import { 
  Container, Row, Col, Card, Button, ButtonGroup, Form, 
  ProgressBar, InputGroup, Spinner, Badge 
} from 'react-bootstrap';
import { FiPlayCircle, FiStopCircle, FiPause, FiSettings, FiMic, FiVideo, FiLink, FiMonitor } from 'react-icons/fi';
import LiveAssistant from '../components/LiveAssistant';

const EnhancedRecording = () => {
  const navigate = useNavigate();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [recordingMode, setRecordingMode] = useState('audio'); // 'audio', 'screen', or 'teams'
  const [audioLevel, setAudioLevel] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showTeamsIntegration, setShowTeamsIntegration] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [uploadProgress, setUploadProgress] = useState(0); // Used in upload progress tracking
  const [teamsStatus, setTeamsStatus] = useState('disconnected'); // 'disconnected', 'connecting', 'connected'
  const [teamsSettings, setTeamsSettings] = useState({
    meetingId: '',
    includeMeetingChat: true,
    recordAttendees: true,
    notifyParticipants: true
  });
  const [recordingSettings, setRecordingSettings] = useState({
    enhanceAudio: true,
    noiseReduction: true,
    normalizeVolume: true,
    captureScreenAudio: true,
    highQuality: true,
    segmentTranscription: true
  });
  
  // Live Assistant state
  const [showLiveAssistant, setShowLiveAssistant] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [liveTranscript, setLiveTranscript] = useState(''); // Used for Phase 2 AI suggestions
  
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioAnalyserRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  
  // Handle recording timer
  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    
    return () => clearInterval(timerRef.current);
  }, [isRecording, isPaused]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMediaTracks();
      clearInterval(timerRef.current);
    };
  }, []);
  
  // Function to update audio level visualizer
  const updateAudioLevel = () => {
    if (!audioAnalyserRef.current) return;
    
    const dataArray = new Uint8Array(audioAnalyserRef.current.frequencyBinCount);
    audioAnalyserRef.current.getByteFrequencyData(dataArray);
    
    // Calculate average volume level
    const average = dataArray.reduce((acc, val) => acc + val, 0) / dataArray.length;
    setAudioLevel(average);
    
    if (isRecording && !isPaused) {
      requestAnimationFrame(updateAudioLevel);
    }
  };
  
  // Start recording function
  const startRecording = async () => {
    try {
      let stream;
      
      // Detect Safari browser
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      if (recordingMode === 'teams') {
        // Handle Teams recording
        await connectToTeamsMeeting();
        return;
      } else if (recordingMode === 'screen') {
        // Safari has limited screen recording support
        if (isSafari) {
          toast.error('Screen recording is not fully supported in Safari. Please use Chrome or Firefox for screen recording, or use Audio Only mode.');
          return;
        }
        // For screen recording
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: { 
            cursor: 'always',
            frameRate: recordingSettings.highQuality ? 30 : 15,
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: recordingSettings.captureScreenAudio
        });
        
        // If user wants audio but didn't allow it through getDisplayMedia, get it separately
        if (recordingSettings.captureScreenAudio && !stream.getAudioTracks().length) {
          const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          audioStream.getAudioTracks().forEach(track => stream.addTrack(track));
        }
      } else {
        // For audio-only recording
        stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: recordingSettings.noiseReduction,
            noiseSuppression: recordingSettings.noiseReduction,
            autoGainControl: recordingSettings.normalizeVolume
          } 
        });
      }
      
      if (stream) {
        streamRef.current = stream;
        
        // Set up audio analysis for visualization
        if (stream.getAudioTracks().length > 0) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
          const source = audioContextRef.current.createMediaStreamSource(stream);
          audioAnalyserRef.current = audioContextRef.current.createAnalyser();
          audioAnalyserRef.current.fftSize = 256;
          source.connect(audioAnalyserRef.current);
          
          updateAudioLevel();
        }
        
        // Create media recorder with appropriate MIME type based on browser support
        let options = {};
        
        // Detect browser for Safari-specific handling
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        
        // Detect supported MIME types - Safari needs MP4, others prefer WebM
        const mimeTypes = isSafari ? [
          'video/mp4',
          'audio/mp4',
          'video/webm',
          'audio/webm'
        ] : [
          'video/webm;codecs=vp9,opus',
          'video/webm;codecs=vp8,opus',
          'video/webm',
          'audio/webm;codecs=opus',
          'audio/webm',
          'video/mp4',  // Fallback for other browsers
          'audio/mp4'
        ];
        
        // Find a supported MIME type
        for (const mimeType of mimeTypes) {
          if (MediaRecorder.isTypeSupported(mimeType)) {
            options = { mimeType };
            console.log(`Using supported MIME type: ${mimeType}`);
            break;
          }
        }
        
        // If no supported type found, try without specifying (browser default)
        if (!options.mimeType) {
          console.warn('No preferred MIME type supported, using browser default');
        }
        
        // Verify we have valid tracks before starting MediaRecorder
        if (recordingMode === 'screen' && !stream.getVideoTracks().length) {
          throw new Error('No video track found for screen recording');
        }
        
        if (!stream.getAudioTracks().length && !stream.getVideoTracks().length) {
          throw new Error('No audio or video tracks available to record');
        }
        
        // Create the MediaRecorder with detected options
        console.log('Creating MediaRecorder with options:', options);
        mediaRecorderRef.current = new MediaRecorder(stream, options);
        
        // Handle data available event
        mediaRecorderRef.current.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunksRef.current.push(e.data);
          }
        };
        
        // Start recording
        mediaRecorderRef.current.start(1000);
        setIsRecording(true);
        setIsPaused(false);
        setDuration(0);
        
        toast.success(`${recordingMode === 'screen' ? 'Screen' : 'Audio'} recording started`);
      }
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording');
    }
  };
  
  // Connect to Microsoft Teams meeting
  const connectToTeamsMeeting = async () => {
    try {
      if (!teamsSettings.meetingId) {
        toast.error('Please enter a Teams meeting ID or link');
        return;
      }
      
      setTeamsStatus('connecting');
      
      // Simulating connection to Teams API
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In production, this would use the Microsoft Teams API to connect to the meeting
      // Example:
      // const teamsClient = new TeamsClient();
      // await teamsClient.connect(teamsSettings.meetingId);
      
      setTeamsStatus('connected');
      toast.success('Connected to Microsoft Teams meeting');
      
      // Start recording through Teams
      setIsRecording(true);
      setDuration(0);
      
      // Start timer for recording duration display
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error connecting to Teams:', error);
      setTeamsStatus('disconnected');
      toast.error('Failed to connect to Teams meeting');
    }
  };
  
  // Disconnect from Teams meeting
  const disconnectFromTeams = async () => {
    try {
      setIsProcessing(true);
      
      // Simulate API call to get recording
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In production, this would use the Microsoft Teams API to get the recording
      // Example:
      // const recording = await teamsClient.getRecording();
      
      toast.success('Successfully obtained Teams recording');
      
      // Create mock form data that would contain the Teams recording
      const formData = new FormData();
      const mockBlob = new Blob(['Teams recording data'], { type: 'audio/mp4' });
      const file = new File([mockBlob], `teams-recording-${new Date().toISOString()}.mp4`, {
        type: 'audio/mp4'
      });
      
      formData.append('recording', file);
      formData.append('recordingType', 'teams');
      formData.append('meetingId', teamsSettings.meetingId);
      formData.append('includedChat', teamsSettings.includeMeetingChat);
      formData.append('recordedAttendees', teamsSettings.recordAttendees);
      
      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No authentication token found');
        toast.error('Authentication required - Please log in');
        return;
      }
      
      // Upload to your server with authentication
      const response = await axios.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        timeout: 120000 // 2 minutes for large files
      });
      
      setIsRecording(false);
      setDuration(0);
      clearInterval(timerRef.current);
      setTeamsStatus('disconnected');
      setIsProcessing(false);
      
      toast.success('Teams recording processed successfully');
      
      // Navigate to the summary
      navigate(`/summary/${response.data.meetingId}`);
      
    } catch (error) {
      console.error('Error processing Teams recording:', error);
      toast.error('Failed to process Teams recording');
      setIsRecording(false);
      setTeamsStatus('disconnected');
      setIsProcessing(false);
    }
  };
  
  // Pause recording function
  const pauseRecording = () => {
    if (recordingMode === 'teams') {
      toast.error('Pausing is not supported for Teams recordings');
      return;
    }
    
    if (mediaRecorderRef.current && isRecording) {
      if (!isPaused) {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
        toast.success('Recording paused');
      } else {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
        toast.success('Recording resumed');
      }
    }
  };
  
  // Stop recording function
  const stopRecording = () => {
    if (recordingMode === 'teams') {
      disconnectFromTeams();
      return;
    }
    
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      // Handle saving recording
      setTimeout(() => {
        processRecording();
      }, 500);
    }
  };
  
  // Clean up media tracks
  const stopMediaTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
  };
  
  // Process recording and upload
  const processRecording = async () => {
    if (chunksRef.current.length === 0) {
      toast.error('No recording data available');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // Get the actual MIME type from the recorder
      const actualMimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
      const fileExtension = actualMimeType.includes('mp4') ? 'mp4' : 'webm';
      
      // Create blob from chunks using the actual MIME type
      const blob = new Blob(chunksRef.current, { type: actualMimeType });
      
      // Create file from blob with correct extension
      const file = new File([blob], `${recordingMode}-recording-${new Date().toISOString()}.${fileExtension}`, {
        type: actualMimeType
      });
      
      // Create form data for upload
      const formData = new FormData();
      formData.append('recording', file);
      formData.append('recordingType', recordingMode);
      
      // Add user ID to associate the recording with the current user
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        if (userData && userData.id) {
          console.log('Adding user ID to recording upload:', userData.id);
          formData.append('userId', userData.id);
        } else {
          console.warn('No user ID found in localStorage');
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
      
      // Add processing options
      Object.keys(recordingSettings).forEach(key => {
        formData.append(key, recordingSettings[key]);
      });
      
      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No authentication token found');
        toast.error('Authentication required - Please log in');
        return;
      }
      
      // Show toast for upload start
      toast('Uploading recording...', {
        icon: '🔄', // Use emoji as an info indicator
        duration: 3000
      });
      console.log('Starting recording upload with authentication');
      
      try {
        // Upload file with authentication and more detailed logging
        const response = await axios.post('/api/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          },
          timeout: 120000, // 2 minute timeout for large files
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload progress: ${percentCompleted}%`);
            setUploadProgress(percentCompleted);
          }
        });
        
        console.log('Upload response:', response.data);
        
        // Show success message with recording ID
        if (response.data && response.data.meetingId) {
          const meetingId = response.data.meetingId;
          toast.success(`Recording saved successfully! ID: ${meetingId.substring(0, 6)}...`);
          
          console.log('Recording uploaded successfully with meeting ID:', meetingId);
          
          // Update analytics before navigating to ensure it shows in recent meetings
          try {
            await axios.post('/api/analytics/update', {
              meetingId: meetingId,
              action: 'meeting_completed'
            });
            console.log('Analytics updated successfully for meeting:', meetingId);
          } catch (analyticsError) {
            console.warn('Failed to update analytics:', analyticsError);
            // Continue anyway - non-critical error
          }
          
          // Navigate to the summary page
          setTimeout(() => {
            navigate(`/summary/${meetingId}`);
          }, 1000);
        } else {
          console.error('Upload succeeded but no meetingId returned:', response.data);
          toast.warning('Recording uploaded but no ID was returned. Check your recent meetings.');
          
          // Navigate to dashboard as fallback
          setTimeout(() => {
            navigate('/dashboard');
          }, 1500);
        }
      } catch (uploadError) {
        console.error('Upload error:', uploadError.response?.data || uploadError.message);
        toast.error(`Upload failed: ${uploadError.response?.data?.message || uploadError.message}`);
      }
      
      // Reset recording state regardless of success/failure
      chunksRef.current = [];
      stopMediaTracks();
      setIsProcessing(false);
      setDuration(0);
      
      // Success toast already shown above, no need for a second one
      // Navigation already handled above, so no need to navigate here
    } catch (error) {
      console.error('Error processing recording:', error);
      toast.error('Failed to process recording');
      setIsProcessing(false);
    }
  };
  
  // Format time for display
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  // Set recording mode
  const setMode = (mode) => {
    if (isRecording) {
      toast.error('Cannot change mode while recording');
      return;
    }
    
    setRecordingMode(mode);
    if (mode === 'teams') {
      setShowTeamsIntegration(true);
    } else {
      setShowTeamsIntegration(false);
    }
  };
  
  // Toggle a setting
  const toggleSetting = (setting) => {
    setRecordingSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };
  
  // Toggle a Teams setting
  const toggleTeamsSetting = (setting) => {
    setTeamsSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };
  
  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="page-title">Recording</h1>
          <p className="text-muted">
            Record audio, screen or Microsoft Teams meetings with advanced processing for optimal transcription quality
          </p>
        </Col>
      </Row>
      
      {/* Mode Selection */}
      <Row className="mb-4">
        <Col className="d-flex justify-content-center">
          <ButtonGroup>
            <Button 
              variant={recordingMode === 'audio' ? 'primary' : 'outline-primary'}
              onClick={() => setMode('audio')}
              disabled={isRecording}
              className="d-flex align-items-center"
            >
              <FiMic className="me-2" /> Audio Only
            </Button>
            <Button 
              variant={recordingMode === 'screen' ? 'primary' : 'outline-primary'}
              onClick={() => setMode('screen')}
              disabled={isRecording}
              className="d-flex align-items-center"
            >
              <FiVideo className="me-2" /> Screen + Audio
            </Button>
            <Button 
              variant={recordingMode === 'teams' ? 'primary' : 'outline-primary'}
              onClick={() => setMode('teams')}
              disabled={isRecording}
              className="d-flex align-items-center"
            >
              <FiMonitor className="me-2" /> Teams Meeting
            </Button>
          </ButtonGroup>
        </Col>
      </Row>
      
      {/* Teams Integration UI */}
      {showTeamsIntegration && (
        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <div className="d-flex align-items-center mb-3">
              <div className={`rounded-circle me-2 d-inline-block ${
                teamsStatus === 'connected' ? 'bg-success' : 
                teamsStatus === 'connecting' ? 'bg-warning' : 'bg-danger'
              }`} style={{width: '10px', height: '10px'}}></div>
              <h5 className="mb-0">
                Microsoft Teams Integration 
                {teamsStatus === 'connected' ? ' - Connected' : 
                 teamsStatus === 'connecting' ? ' - Connecting...' : ' - Disconnected'}
              </h5>
            </div>
            
            <Form.Group className="mb-3">
              <Form.Label>Teams Meeting ID or Link</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  value={teamsSettings.meetingId}
                  onChange={(e) => setTeamsSettings({...teamsSettings, meetingId: e.target.value})}
                  placeholder="Enter meeting ID or paste meeting link"
                  disabled={teamsStatus !== 'disconnected' || isRecording}
                />
                <Button 
                  variant="outline-secondary"
                  onClick={() => navigator.clipboard.readText().then(text => 
                    setTeamsSettings({...teamsSettings, meetingId: text})
                  )}
                  disabled={teamsStatus !== 'disconnected' || isRecording}
                >
                  <FiLink className="me-1" /> Paste
                </Button>
              </InputGroup>
            </Form.Group>
            
            <Row className="mb-3">
              <Col md={4}>
                <Form.Check 
                  type="switch"
                  id="includeMeetingChat"
                  label="Include Meeting Chat"
                  checked={teamsSettings.includeMeetingChat}
                  onChange={() => toggleTeamsSetting('includeMeetingChat')}
                  disabled={isRecording}
                />
              </Col>
              <Col md={4}>
                <Form.Check 
                  type="switch"
                  id="recordAttendees"
                  label="Record Attendees"
                  checked={teamsSettings.recordAttendees}
                  onChange={() => toggleTeamsSetting('recordAttendees')}
                  disabled={isRecording}
                />
              </Col>
              <Col md={4}>
                <Form.Check 
                  type="switch"
                  id="notifyParticipants"
                  label="Notify Participants"
                  checked={teamsSettings.notifyParticipants}
                  onChange={() => toggleTeamsSetting('notifyParticipants')}
                  disabled={isRecording}
                />
              </Col>
            </Row>
            
            <div className="small text-muted">
              <strong>Note:</strong> This integration connects directly with Microsoft Teams to record meetings automatically. 
              Connected recordings will be processed using the enhanced audio settings below.
            </div>
          </Card.Body>
        </Card>
      )}
      
      {/* Recording Visualizer */}
      <Card className="mb-4 text-center shadow-sm">
        <Card.Body className="py-5">
          <h2 className="display-4 fw-bold mb-4">{formatTime(duration)}</h2>
          
          {/* Audio Level Visualizer */}
          {recordingMode !== 'teams' && (
            <div className="mb-4">
              <ProgressBar 
                now={audioLevel / 2} 
                variant="primary" 
                className="mb-2" 
                style={{height: '1.5rem'}}
              />
            </div>
          )}
          
          {/* Teams Status Display */}
          {recordingMode === 'teams' && teamsStatus === 'connected' && (
            <div className="d-flex align-items-center justify-content-center mb-4">
              <div className="spinner-grow spinner-grow-sm text-success me-2" role="status">
                <span className="visually-hidden">Recording...</span>
              </div>
              <span className="text-success">Recording Teams Meeting</span>
            </div>
          )}
          
          {/* Recording Controls */}
          <div className="d-flex justify-content-center gap-3">
            {!isRecording ? (
              <Button 
                variant="danger"
                size="lg"
                className="rounded-circle p-3"
                style={{width: '64px', height: '64px'}}
                onClick={startRecording}
                disabled={isProcessing || (recordingMode === 'teams' && !teamsSettings.meetingId)}
              >
                <FiPlayCircle size={24} />
              </Button>
            ) : (
              <>
                {recordingMode !== 'teams' && (
                  <Button 
                    variant="warning"
                    size="lg"
                    className="rounded-circle p-3"
                    style={{width: '64px', height: '64px'}}
                    onClick={pauseRecording}
                    disabled={isProcessing}
                  >
                    {isPaused ? <FiPlayCircle size={24} /> : <FiPause size={24} />}
                  </Button>
                )}
                
                <Button 
                  variant="danger"
                  size="lg"
                  className="rounded-circle p-3"
                  style={{width: '64px', height: '64px'}}
                  onClick={stopRecording}
                  disabled={isProcessing}
                >
                  <FiStopCircle size={24} />
                </Button>
              </>
            )}
          </div>
          
          {isProcessing && (
            <div className="mt-4">
              <Spinner animation="border" variant="primary" className="mb-2" />
              <p>Processing your recording...</p>
            </div>
          )}
          
          {/* Live Assistant Toggle */}
          <div className="mt-4">
            <Button
              variant={showLiveAssistant ? 'success' : 'outline-primary'}
              onClick={() => setShowLiveAssistant(!showLiveAssistant)}
              className="d-flex align-items-center mx-auto"
            >
              <span className="me-2" role="img" aria-label="robot">🤖</span>
              {showLiveAssistant ? 'Live Assistant Active' : 'Enable Live Assistant'}
              {showLiveAssistant && isRecording && (
                <Badge bg="light" text="dark" className="ms-2">LIVE</Badge>
              )}
            </Button>
            <p className="text-muted small mt-2 mb-0">
              Real-time transcription and AI assistance during your recording
            </p>
          </div>
        </Card.Body>
      </Card>
      
      {/* Settings Panel */}
      <Card className="shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Recording Settings</h5>
          <FiSettings />
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Check 
                type="switch"
                id="enhanceAudio"
                label="Enhance Audio"
                checked={recordingSettings.enhanceAudio}
                onChange={() => toggleSetting('enhanceAudio')}
                className="mb-3"
              />
              
              <Form.Check 
                type="switch"
                id="noiseReduction"
                label="Noise Reduction"
                checked={recordingSettings.noiseReduction}
                onChange={() => toggleSetting('noiseReduction')}
                className="mb-3"
              />
              
              <Form.Check 
                type="switch"
                id="normalizeVolume"
                label="Normalize Volume"
                checked={recordingSettings.normalizeVolume}
                onChange={() => toggleSetting('normalizeVolume')}
                className="mb-3"
              />
            </Col>
            
            <Col md={6}>
              {recordingMode === 'screen' && (
                <Form.Check 
                  type="switch"
                  id="captureScreenAudio"
                  label="Capture Screen Audio"
                  checked={recordingSettings.captureScreenAudio}
                  onChange={() => toggleSetting('captureScreenAudio')}
                  className="mb-3"
                />
              )}
              
              <Form.Check 
                type="switch"
                id="highQuality"
                label="High Quality"
                checked={recordingSettings.highQuality}
                onChange={() => toggleSetting('highQuality')}
                className="mb-3"
              />
              
              <Form.Check 
                type="switch"
                id="segmentTranscription"
                label="Segment Transcription"
                checked={recordingSettings.segmentTranscription}
                onChange={() => toggleSetting('segmentTranscription')}
                className="mb-3"
              />
            </Col>
          </Row>
          
          <div className="small text-muted mt-3">
            <p className="mb-2">
              <strong>Enhanced Audio:</strong> Applies audio processing optimized for speech recognition.
            </p>
            <p className="mb-2">
              <strong>Segment Transcription:</strong> Analyzes different parts of the recording to find optimal speech content.
            </p>
            <p className="mb-0">
              <strong>Note:</strong> Screen recordings with optimized audio are ideal for capturing YouTube videos and online meetings.
            </p>
          </div>
        </Card.Body>
      </Card>
      
      {/* Live Assistant Floating Overlay */}
      {showLiveAssistant && (
        <LiveAssistant
          isRecording={isRecording}
          onClose={() => setShowLiveAssistant(false)}
          onTranscriptUpdate={(transcript) => setLiveTranscript(transcript)}
          initialPosition={{ x: window.innerWidth - 420, y: 100 }}
        />
      )}
    </Container>
  );
};

export default EnhancedRecording;
